import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getAllInvites, getInviteStats, createInvite, revokeInvite, resendInvite as resend, deleteInvite } from '../../services/inviteService';

const ADMIN_SESSION_KEY = 'vf_admin_session';

const checkAdminSession = () => {
  if (typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(ADMIN_SESSION_KEY) === '1';
};

const ROLES = [
  { value: 'creator', label: 'Creator', description: 'Album owner - can manage their album' },
  { value: 'invitee', label: 'Invitee', description: 'View access only' },
];

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[AdminInvites] Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <main style={{ minHeight: '100vh', background: '#111', color: '#fff', padding: '2rem' }}>
          <div style={{ background: '#7f1d1d', border: '1px solid #ef4444', borderRadius: 8, padding: '1.5rem', maxWidth: 600, margin: '0 auto' }}>
            <h2 style={{ color: '#fca5a5', marginBottom: '1rem' }}>⚠️ Error Loading Invites</h2>
            <p style={{ color: '#fecaca', fontSize: '0.875rem', marginBottom: '1rem' }}>{this.state.error?.message || 'An unexpected error occurred'}</p>
            <button onClick={() => window.location.reload()} style={{ padding: '0.5rem 1rem', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Reload Page</button>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}

export function AdminInvites() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [invites, setInvites] = useState([]);
  const [filteredInvites, setFilteredInvites] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [creatingInvite, setCreatingInvite] = useState(false);
  const [newInvite, setNewInvite] = useState({
    email: '',
    phone: '',
    whatsapp: '',
    albumId: 'sushflix',
    role: 'invitee',
    expiry: '30d',
  });
  const [creating, setCreating] = useState(false);

  const [selectedInvite, setSelectedInvite] = useState(null);
  const [inviteLink, setInviteLink] = useState('');

  useEffect(() => {
    try {
      const isAuth = checkAdminSession();
      if (isAuth) {
        setIsAuthenticated(true);
      } else {
        navigate('/admin', { replace: true });
      }
    } catch (err) {
      console.error('[AdminInvites] Auth check failed:', err);
      setError(err.message);
      navigate('/admin', { replace: true });
    }
    setLoading(false);
  }, [navigate]);

  const fetchInvites = useCallback(async () => {
    setFetching(true);
    setError(null);
    
    try {
      const inviteList = await getAllInvites({ limit: 200 });
      setInvites(inviteList);
      setFilteredInvites(inviteList);
    } catch (err) {
      console.error('Error fetching invites:', err);
      setError(err.message);
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchInvites();
    }
  }, [isAuthenticated, fetchInvites]);

  useEffect(() => {
    let filtered = invites;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(i => 
        i.email?.toLowerCase().includes(term) ||
        i.phone?.includes(term) ||
        i.token?.toLowerCase().includes(term)
      );
    }
    
    if (statusFilter) {
      filtered = filtered.filter(i => {
        if (statusFilter === 'active') return !i.used && !isExpired(i.expiresAt);
        if (statusFilter === 'used') return i.used;
        if (statusFilter === 'expired') return !i.used && isExpired(i.expiresAt);
        return true;
      });
    }
    
    setFilteredInvites(filtered);
  }, [invites, searchTerm, statusFilter]);

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return expiresAt <= Date.now();
  };

  const handleLogout = () => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(ADMIN_SESSION_KEY);
    }
    navigate('/admin', { replace: true });
  };

  const handleCreateInvite = async () => {
    setCreating(true);
    setError(null);
    
    try {
      const result = await createInvite({
        ...newInvite,
        createdBy: 'admin',
      });
      
      const link = `${window.location.origin}/invite/${result.token}`;
      setInviteLink(link);
      setCreatingInvite(false);
      setNewInvite({
        email: '',
        phone: '',
        whatsapp: '',
        albumId: 'sushflix',
        role: 'invitee',
        expiry: '30d',
      });
      fetchInvites();
    } catch (err) {
      console.error('Error creating invite:', err);
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    try {
      await revokeInvite(inviteId);
      fetchInvites();
    } catch (err) {
      console.error('Error revoking invite:', err);
      setError(err.message);
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      const result = await resend(inviteId);
      const link = `${window.location.origin}/invite/${result.token}`;
      setInviteLink(link);
      setSelectedInvite(inviteId);
      fetchInvites();
    } catch (err) {
      console.error('Error resending invite:', err);
      setError(err.message);
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    if (!confirm('Are you sure you want to delete this invite?')) return;
    try {
      await deleteInvite(inviteId);
      fetchInvites();
    } catch (err) {
      console.error('Error deleting invite:', err);
      setError(err.message);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getStatusBadge = (invite) => {
    if (invite.used) {
      return { color: '#16a34a', label: 'Used' };
    }
    if (isExpired(invite.expiresAt)) {
      return { color: '#ef4444', label: 'Expired' };
    }
    return { color: '#f59e0b', label: 'Active' };
  };

  const EXPIRY_OPTIONS = [
    { value: '24h', label: '24 hours' },
    { value: '7d', label: '7 days' },
    { value: '30d', label: '30 days (default)' },
    { value: '1y', label: '1 year' },
    { value: 'never', label: 'Never expire' },
  ];

  if (loading) {
    return (
      <main style={{ minHeight: '100vh', background: '#111', color: '#fff', display: 'grid', placeItems: 'center' }}>
        <p>Loading...</p>
      </main>
    );
  }

  if (!isAuthenticated) {
    return (
      <main style={{ minHeight: '100vh', background: '#111', color: '#fff', display: 'grid', placeItems: 'center' }}>
        <p style={{ color: '#666' }}>Redirecting to login...</p>
      </main>
    );
  }

  const TABS = [
    { id: 'dashboard', label: 'Dashboard', path: '/admin' },
    { id: 'media', label: 'Media Upload', path: '/admin/media' },
    { id: 'invites', label: 'Invites', path: '/admin/invites' },
    { id: 'users', label: 'Users', path: '/admin/users' },
    { id: 'albums', label: 'Albums', path: '/admin/albums' },
    { id: 'settings', label: 'Settings', path: '/admin/settings' },
  ];

  return (
    <ErrorBoundary>
      <main style={{ minHeight: '100vh', background: '#111', color: '#fff' }}>
        {error && (
          <div style={{ background: '#7f1d1d', padding: '1rem', borderBottom: '1px solid #ef4444', textAlign: 'center' }}>
            <p style={{ color: '#fecaca', margin: 0 }}>⚠️ {error}</p>
          </div>
        )}
        
        <header style={{ background: '#1a1a1a', borderBottom: '1px solid #333', padding: '0 1.5rem' }}>
          <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 60 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Link to="/admin" style={{ textDecoration: 'none' }}>
                <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#e50914' }}>VEEFLIX</span>
              </Link>
              <span style={{ color: '#666', fontSize: '0.9rem' }}>Admin</span>
            </div>
            <button onClick={handleLogout} style={{ padding: '0.4rem 0.8rem', borderRadius: 6, border: '1px solid #444', background: 'transparent', color: '#9ca3af', fontSize: '0.8rem', cursor: 'pointer' }}>Logout</button>
          </div>
        </header>

        <div style={{ maxWidth: 1400, margin: '0 auto', padding: '1.5rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            {TABS.map(tab => (
              <Link
                key={tab.id}
                to={tab.path}
                style={{
                  padding: '0.5rem 0.9rem',
                  borderRadius: 6,
                  textDecoration: 'none',
                  color: tab.id === 'invites' ? '#fff' : '#9ca3af',
                  background: tab.id === 'invites' ? '#333' : 'transparent',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                {tab.label}
              </Link>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h1 style={{ margin: 0 }}>Invites</h1>
            <button onClick={() => setCreatingInvite(true)} style={{ padding: '0.5rem 1rem', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>+ Create Invite</button>
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Search by email or token..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ padding: '0.6rem 1rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff', minWidth: 250, flex: 1 }}
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ padding: '0.6rem 1rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff' }}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="used">Used</option>
              <option value="expired">Expired</option>
            </select>
            <button onClick={fetchInvites} disabled={fetching} style={{ padding: '0.6rem 1rem', background: '#333', color: '#fff', border: 'none', borderRadius: 6, cursor: fetching ? 'not-allowed' : 'pointer' }}>
              {fetching ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          <div style={{ background: '#1a1a1a', borderRadius: 10, padding: '1rem', marginBottom: '1.5rem' }}>
            <p style={{ color: '#9ca3af', fontSize: '0.875rem', margin: 0 }}>
              {filteredInvites.length} invite{filteredInvites.length !== 1 ? 's' : ''} found
            </p>
          </div>

          <div style={{ background: '#1a1a1a', borderRadius: 10, padding: '0', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '0.75rem 1rem', borderBottom: '1px solid #333', color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>Invitee</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid #333', color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>Album</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid #333', color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>Role</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid #333', color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid #333', color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>Expires</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid #333', color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>Created</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem', borderBottom: '1px solid #333', color: '#9ca3af', fontSize: '0.75rem', textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvites.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                      {fetching ? 'Loading invites...' : 'No invites found'}
                    </td>
                  </tr>
                ) : (
                  filteredInvites.map(invite => {
                    const status = getStatusBadge(invite);
                    return (
                      <tr key={invite.id}>
                        <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #222' }}>
                          <div>{invite.email || invite.phone || 'No contact'}</div>
                          {invite.whatsapp && <div style={{ fontSize: '0.75rem', color: '#25D366' }}>WA: {invite.whatsapp}</div>}
                        </td>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #222' }}>{invite.albumSlug || invite.albumId}</td>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #222' }}>
                          <span style={{ background: '#333', color: '#fff', padding: '0.2rem 0.5rem', borderRadius: 4, fontSize: '0.75rem' }}>
                            {invite.role || 'invitee'}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #222' }}>
                          <span style={{ background: status.color, color: '#fff', padding: '0.2rem 0.5rem', borderRadius: 4, fontSize: '0.75rem' }}>
                            {status.label}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #222', color: '#9ca3af' }}>{formatDate(invite.expiresAt)}</td>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #222', color: '#9ca3af' }}>{formatDate(invite.createdAt)}</td>
                        <td style={{ padding: '0.75rem', borderBottom: '1px solid #222' }}>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button onClick={() => handleResendInvite(invite.id)} disabled={invite.used} title="Resend" style={{ padding: '0.3rem 0.5rem', background: '#333', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.7rem', cursor: invite.used ? 'not-allowed' : 'pointer' }}>↻</button>
                            <button onClick={() => handleRevokeInvite(invite.id)} disabled={invite.used || isExpired(invite.expiresAt)} title="Revoke" style={{ padding: '0.3rem 0.5rem', background: '#333', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.7rem', cursor: invite.used || isExpired(invite.expiresAt) ? 'not-allowed' : 'pointer' }}>✕</button>
                            <button onClick={() => handleDeleteInvite(invite.id)} title="Delete" style={{ padding: '0.3rem 0.5rem', background: '#7f1d1d', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.7rem', cursor: 'pointer' }}>🗑</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {creatingInvite && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#1a1a1a', borderRadius: 12, padding: '2rem', maxWidth: 500, width: '90%' }}>
                <h2 style={{ marginTop: 0 }}>Create Invite</h2>
                
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9ca3af' }}>Email (optional)</label>
                  <input type="email" value={newInvite.email} onChange={(e) => setNewInvite({ ...newInvite, email: e.target.value })} placeholder="user@example.com" style={{ width: '100%', padding: '0.6rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff' }} />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9ca3af' }}>Phone for WhatsApp (optional)</label>
                  <input type="tel" value={newInvite.phone} onChange={(e) => setNewInvite({ ...newInvite, phone: e.target.value })} placeholder="+911234567890" style={{ width: '100%', padding: '0.6rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff' }} />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9ca3af' }}>WhatsApp Number (optional)</label>
                  <input type="tel" value={newInvite.whatsapp} onChange={(e) => setNewInvite({ ...newInvite, whatsapp: e.target.value })} placeholder="+911234567890" style={{ width: '100%', padding: '0.6rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff' }} />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9ca3af' }}>Album</label>
                  <select value={newInvite.albumId} onChange={(e) => setNewInvite({ ...newInvite, albumId: e.target.value })} style={{ width: '100%', padding: '0.6rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff' }}>
                    <option value="sushflix">Sushflix</option>
                  </select>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9ca3af' }}>Role</label>
                  <select value={newInvite.role} onChange={(e) => setNewInvite({ ...newInvite, role: e.target.value })} style={{ width: '100%', padding: '0.6rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff' }}>
                    {ROLES.map(role => (
                      <option key={role.value} value={role.value}>{role.label} - {role.description}</option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9ca3af' }}>Expires In</label>
                  <select value={newInvite.expiry} onChange={(e) => setNewInvite({ ...newInvite, expiry: e.target.value })} style={{ width: '100%', padding: '0.6rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff' }}>
                    {EXPIRY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button onClick={() => setCreatingInvite(false)} style={{ flex: 1, padding: '0.75rem', background: '#333', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={handleCreateInvite} disabled={creating || (!newInvite.email && !newInvite.phone)} style={{ flex: 1, padding: '0.75rem', background: creating ? '#444' : '#16a34a', color: '#fff', border: 'none', borderRadius: 6, cursor: creating || (!newInvite.email && !newInvite.phone) ? 'not-allowed' : 'pointer' }}>
                    {creating ? 'Creating...' : 'Create Invite'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {inviteLink && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#1a1a1a', borderRadius: 12, padding: '2rem', maxWidth: 500, width: '90%' }}>
                <h2 style={{ marginTop: 0, color: '#16a34a' }}>✓ Invite Created!</h2>
                <p style={{ color: '#9ca3af', marginBottom: '1rem' }}>Share this link with the invitee:</p>
                <input type="text" value={inviteLink} readOnly style={{ width: '100%', padding: '0.6rem', borderRadius: 6, border: '1px solid #333', background: '#111', color: '#fff', marginBottom: '1rem', fontFamily: 'monospace', fontSize: '0.8rem' }} />
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button onClick={() => copyToClipboard(inviteLink)} style={{ flex: 1, padding: '0.75rem', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Copy Link</button>
                  <button onClick={() => setInviteLink('')} style={{ flex: 1, padding: '0.75rem', background: '#333', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Done</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </ErrorBoundary>
  );
}

export default AdminInvites;